import { normalizeOutreachStepDelayInMinutes } from '@apps/web/lib/outreach_step_delay_utils';
import { type OutreachType } from '@apps/web/lib/outreach_step_types';
import {
    DEFAULT_RECURRING_TEMPLATES,
    MULTI_SHIFT_ALL_SHIFTS_SMS_TEMPLATE,
    type OutreachStep,
    WELLSKY_DEFAULT_RECURRING_TEMPLATES,
} from '@apps/web/lib/outreach_utils';

export type { OutreachType } from '@apps/web/lib/outreach_step_types';

export type EscalationPolicy = 'sms_only' | 'voice_only' | 'sms_and_voice';

const DEFAULT_CALLOUT_SINGLE_SHIFT_SMS_TEMPLATE =
    'Hey {caregiver_first_name}, we have an open shift - {shift_date} / ' +
    '{shift_start_time} to {shift_end_time}{location_text}. Are you available to pick it up?';

const DEFAULT_CALLOUT_MULTI_SHIFT_SMS_TEMPLATE =
    'Hey {caregiver_first_name}, we have {shift_count} open shifts:\n\n' +
    '{shift_list}\n\nAre you available for any of the shifts listed above? ' +
    'If so, let us know which shift(s)!';

const DEFAULT_CALLOUT_MULTI_SHIFT_ITEM_TEMPLATE =
    '{shift_date}, {shift_start_time}-{shift_end_time}{location_text} {caregiver_distance}\n{previous_visits}';

export const DEFAULT_RECURRING_SHIFT_SMS_TEMPLATE =
    'Hey {caregiver_first_name}, we have a recurring shift on ' +
    '{shift_day_of_week}s, starting {shift_date} from {shift_start_time} to ' +
    '{shift_end_time}{location_text} {caregiver_distance}. Here is some information about the client:\n' +
    '{care_plan_summary}\n' +
    'Are you available to take this shift on {shift_day_of_week}s moving forward?';

export const WELLSKY_DEFAULT_RECURRING_SHIFT_SMS_TEMPLATE =
    'Hey {caregiver_first_name}, we have a recurring shift ' +
    '{shift_frequency}, starting {shift_date} from {shift_start_time} to ' +
    '{shift_end_time}{location_text} {caregiver_distance}. Here is some information about the client:\n' +
    '{care_plan_summary}\n' +
    'Are you available to take this shift {shift_frequency} moving forward?';

const DEFAULT_CLOCK_IN_SMS_TEMPLATE =
    'Hi {caregiver_first_name}, your shift started at {shift_start_time}, but it ' +
    'does not seem you clocked in. Please do so as soon as you can!';

const DEFAULT_CLOCK_OUT_SMS_TEMPLATE =
    'Hi {caregiver_first_name}, your shift ended at {shift_end_time}, but it does ' +
    'not seem you clocked out. Please complete your care tasks ' +
    'and clock out as soon as you can!';

const DEFAULT_CALLOUT_SINGLE_SHIFT_OPENING_LINE =
    "I'm calling to see if you are interested in picking up a" +
    ' shift {shift_date} {location_text} {caregiver_distance}' +
    ' {previous_visits} - can I tell you more?';

const DEFAULT_CALLOUT_SINGLE_SHIFT_OPENING_LINE_NEW_ORG =
    "I'm calling to see if you are interested in picking up a" +
    ' shift {shift_date} {location_text} {caregiver_distance}' +
    ' {previous_visits}\ncan I tell you more?';

const DEFAULT_CALLOUT_MULTI_SHIFT_OPENING_LINE =
    "I'm calling because we have {shift_count} open shifts - can I tell you more?";

export const DEFAULT_RECURRING_SHIFT_SINGLE_SHIFT_OPENING_LINE =
    "I'm calling to see if you're interested in taking on a" +
    ' recurring shift on {shift_day_of_week}s {location_text}' +
    ' {caregiver_distance} {previous_visits}' +
    ' - can I tell you more?';

export const WELLSKY_DEFAULT_RECURRING_SHIFT_SINGLE_SHIFT_OPENING_LINE =
    "I'm calling to see if you're interested in taking on a" +
    ' recurring shift {shift_frequency} {location_text}' +
    ' {caregiver_distance} {previous_visits}' +
    ' - can I tell you more?';

const DEFAULT_RECURRING_SHIFT_MULTI_SHIFT_OPENING_LINE =
    "I'm calling because we have {shift_count} recurring shifts" +
    ' available - can I tell you more?';

const DEFAULT_SHIFT_CONFIRMATION_SINGLE_SHIFT_OPENING_LINE =
    "I'm calling to confirm your upcoming shift - can I confirm your availability?";

const DEFAULT_SHIFT_CONFIRMATION_MULTI_SHIFT_OPENING_LINE =
    "I'm calling to confirm your upcoming shifts - can I confirm your availability?";

const MULTI_SHIFT_SAFE_OPENING_LINE_PLACEHOLDERS = ['{shift_count}'];

const SINGLE_SHIFT_OPENING_LINE_PLACEHOLDERS = [
    '{shift_date}',
    '{shift_day_of_week}',
    '{shift_start_time}',
    '{shift_end_time}',
    '{location_text}',
    '{caregiver_distance}',
    '{previous_visits}',
];

export function getCurrentEscalationPolicy(
    steps: ReadonlyArray<OutreachStep>,
): EscalationPolicy {
    const hasSms = steps.some((step) => step.step_type === 'sms');
    const hasVoice = steps.some((step) => step.step_type === 'voice');

    if (hasSms && hasVoice) {
        return 'sms_and_voice';
    }
    if (hasSms) {
        return 'sms_only';
    }
    if (hasVoice) {
        return 'voice_only';
    }
    return 'sms_and_voice';
}

// A missing key must read as off: the clock reminder handlers only mention EVV
// when `mention_evv` is present and truthy.
export function getMentionEvvFromMetadata(
    metadataSchema: Record<string, unknown> | undefined,
): boolean {
    return metadataSchema?.['mention_evv'] === true;
}

export function getTemplateVoiceOpeningLine({
    metadataSchema,
    outreachType,
    isMultiShift,
}: {
    readonly metadataSchema: Record<string, unknown> | undefined;
    readonly outreachType: OutreachType;
    readonly isMultiShift: boolean;
}): string | null {
    const singleShiftOpeningLine =
        typeof metadataSchema?.['opening_line'] === 'string' &&
        metadataSchema['opening_line'].trim().length > 0
            ? metadataSchema['opening_line']
            : null;
    const multiShiftOpeningLine =
        typeof metadataSchema?.['multi_shift_opening_line'] === 'string' &&
        metadataSchema['multi_shift_opening_line'].trim().length > 0
            ? metadataSchema['multi_shift_opening_line']
            : null;

    if (usesSplitVoiceOpeningLine(outreachType, isMultiShift)) {
        return (
            multiShiftOpeningLine ??
            getLegacyMultiShiftOpeningLine({
                openingLine: singleShiftOpeningLine,
                outreachType,
            })
        );
    }

    return singleShiftOpeningLine;
}

export function usesSplitVoiceOpeningLine(
    outreachType: OutreachType,
    isMultiShift: boolean,
): boolean {
    return (
        (outreachType === 'callout' || outreachType === 'recurring_shift') &&
        isMultiShift
    );
}

function isDefaultSingleShiftOpeningLine({
    openingLine,
    outreachType,
}: {
    readonly openingLine: string;
    readonly outreachType: OutreachType;
}): boolean {
    const defaultSingleShiftLines = new Set([
        getDefaultVoiceOpeningLine({
            outreachType,
            isMultiShift: false,
        }).trim(),
    ]);
    if (outreachType === 'callout') {
        defaultSingleShiftLines.add(
            DEFAULT_CALLOUT_SINGLE_SHIFT_OPENING_LINE_NEW_ORG.trim(),
        );
    } else if (outreachType === 'recurring_shift') {
        defaultSingleShiftLines.add(
            WELLSKY_DEFAULT_RECURRING_SHIFT_SINGLE_SHIFT_OPENING_LINE.trim(),
        );
    }
    return defaultSingleShiftLines.has(openingLine.trim());
}

function getLegacyMultiShiftOpeningLine({
    openingLine,
    outreachType,
}: {
    readonly openingLine: string | null | undefined;
    readonly outreachType: OutreachType;
}): string | null {
    const trimmedOpeningLine = openingLine?.trim();
    if (
        !trimmedOpeningLine ||
        !usesSplitVoiceOpeningLine(outreachType, true) ||
        isDefaultSingleShiftOpeningLine({
            openingLine: trimmedOpeningLine,
            outreachType,
        }) ||
        !MULTI_SHIFT_SAFE_OPENING_LINE_PLACEHOLDERS.some((placeholder) =>
            trimmedOpeningLine.includes(placeholder),
        ) ||
        SINGLE_SHIFT_OPENING_LINE_PLACEHOLDERS.some((placeholder) =>
            trimmedOpeningLine.includes(placeholder),
        )
    ) {
        return null;
    }
    return openingLine ?? null;
}

export function getDefaultVoiceOpeningLine({
    outreachType,
    isMultiShift,
    supportsShiftRecurrence = false,
}: {
    readonly outreachType: OutreachType;
    readonly isMultiShift: boolean;
    readonly supportsShiftRecurrence?: boolean;
}): string {
    if (outreachType === 'recurring_shift') {
        return isMultiShift
            ? DEFAULT_RECURRING_SHIFT_MULTI_SHIFT_OPENING_LINE
            : supportsShiftRecurrence
              ? WELLSKY_DEFAULT_RECURRING_SHIFT_SINGLE_SHIFT_OPENING_LINE
              : DEFAULT_RECURRING_SHIFT_SINGLE_SHIFT_OPENING_LINE;
    }

    if (outreachType === 'shift_confirmation') {
        return isMultiShift
            ? DEFAULT_SHIFT_CONFIRMATION_MULTI_SHIFT_OPENING_LINE
            : DEFAULT_SHIFT_CONFIRMATION_SINGLE_SHIFT_OPENING_LINE;
    }

    if (outreachType === 'callout' && isMultiShift) {
        return DEFAULT_CALLOUT_MULTI_SHIFT_OPENING_LINE;
    }

    if (outreachType === 'callout') {
        return DEFAULT_CALLOUT_SINGLE_SHIFT_OPENING_LINE;
    }

    return '';
}

export function normalizeOutreachStepDelays(
    steps: ReadonlyArray<OutreachStep>,
    outreachType: OutreachType,
): Array<OutreachStep> {
    return steps.map((step, index) => ({
        ...step,
        delay_in_minutes: normalizeOutreachStepDelayInMinutes({
            index,
            outreachType,
            delayInMinutes: step.delay_in_minutes,
        }),
    }));
}

export function getVisibleOutreachSteps(
    steps: ReadonlyArray<OutreachStep>,
    _templateSteps: ReadonlyArray<OutreachStep> | undefined,
    outreachType: OutreachType,
): Array<OutreachStep> {
    return normalizeOutreachStepDelays([...steps], outreachType);
}

function getFallbackOutreachStepsForType(
    stepType: OutreachStep['step_type'],
    steps: ReadonlyArray<OutreachStep>,
    originalSteps: ReadonlyArray<OutreachStep> | undefined,
    templateSteps: ReadonlyArray<OutreachStep> | undefined,
): Array<OutreachStep> {
    const currentSteps = steps.filter((step) => step.step_type === stepType);
    if (currentSteps.length > 0) {
        return currentSteps;
    }

    const originalTypeSteps =
        originalSteps?.filter((step) => step.step_type === stepType) ?? [];
    if (originalTypeSteps.length > 0) {
        return originalTypeSteps;
    }

    return templateSteps?.filter((step) => step.step_type === stepType) ?? [];
}

function needsStepType(
    stepType: OutreachStep['step_type'],
    policy: EscalationPolicy,
): boolean {
    if (policy === 'sms_and_voice') {
        return true;
    }
    if (policy === 'sms_only') {
        return stepType === 'sms';
    }
    return stepType === 'voice';
}

function pickOrderSource({
    policy,
    steps,
    originalSteps,
    templateSteps,
}: {
    readonly policy: EscalationPolicy;
    readonly steps: ReadonlyArray<OutreachStep>;
    readonly originalSteps: ReadonlyArray<OutreachStep> | undefined;
    readonly templateSteps: ReadonlyArray<OutreachStep> | undefined;
}): ReadonlyArray<OutreachStep> {
    const candidates = [steps, originalSteps ?? [], templateSteps ?? []];

    const sourceWithAllNeededTypes = candidates.find((candidate) => {
        if (policy === 'sms_and_voice') {
            return (
                candidate.some((step) => step.step_type === 'sms') &&
                candidate.some((step) => step.step_type === 'voice')
            );
        }

        return candidate.some((step) => needsStepType(step.step_type, policy));
    });
    if (sourceWithAllNeededTypes) {
        return sourceWithAllNeededTypes;
    }

    return (
        candidates.find((candidate) =>
            candidate.some((step) => needsStepType(step.step_type, policy)),
        ) ?? []
    );
}

function takeNextStepOfType({
    typeSteps,
}: {
    readonly typeSteps: Array<OutreachStep>;
}): OutreachStep | undefined {
    return typeSteps.shift();
}

function createDefaultOutreachStep(
    stepType: OutreachStep['step_type'],
    outreachType: OutreachType,
    steps: ReadonlyArray<IdentifiedOutreachStep>,
    originalSteps: ReadonlyArray<IdentifiedOutreachStep> | undefined,
    templateSteps: ReadonlyArray<IdentifiedOutreachStep> | undefined,
    supportsShiftRecurrence = false,
): OutreachStep {
    const fallbackStep = [
        ...steps,
        ...(originalSteps ?? []),
        ...(templateSteps ?? []),
    ]
        .reverse()
        .find((step) => step.step_type === stepType);

    if (fallbackStep) {
        // Clone the copy the editor shows, never the stored row state it
        // does not: a cloned id would make the server read this brand-new
        // step as an edit of the one it came from (or refuse the list as
        // duplicate), and a cloned incentive would promise money the
        // coordinator never typed and cannot see here to remove.
        const {
            id: _id,
            ordinal: _ordinal,
            opening_line: _openingLine,
            incentive_amount_usd: _incentiveAmountUsd,
            incentive_description: _incentiveDescription,
            ...clonedEditorCopy
        } = fallbackStep;
        return {
            ...clonedEditorCopy,
            delay_in_minutes: 10,
        };
    }

    if (stepType === 'voice') {
        return {
            step_type: 'voice',
            delay_in_minutes: 10,
            prompt_alteration: null,
            ...(outreachType === 'callout' || outreachType === 'recurring_shift'
                ? { multi_shift_prompt_alteration: null }
                : {}),
        };
    }

    if (outreachType === 'recurring_shift') {
        const recurringTemplates = supportsShiftRecurrence
            ? WELLSKY_DEFAULT_RECURRING_TEMPLATES
            : DEFAULT_RECURRING_TEMPLATES;
        return {
            step_type: 'sms',
            delay_in_minutes: 10,
            message_template: supportsShiftRecurrence
                ? WELLSKY_DEFAULT_RECURRING_SHIFT_SMS_TEMPLATE
                : DEFAULT_RECURRING_SHIFT_SMS_TEMPLATE,
            group_template: recurringTemplates.clientTemplate,
            per_group_template: recurringTemplates.groupTemplate,
            multi_shift_item_template: recurringTemplates.itemTemplate,
        };
    }

    if (outreachType === 'clock_in' || outreachType === 'clock_out') {
        return {
            step_type: 'sms',
            delay_in_minutes: 10,
            message_template:
                outreachType === 'clock_in'
                    ? DEFAULT_CLOCK_IN_SMS_TEMPLATE
                    : DEFAULT_CLOCK_OUT_SMS_TEMPLATE,
        };
    }

    return {
        step_type: 'sms',
        delay_in_minutes: 10,
        callout_template: DEFAULT_CALLOUT_SINGLE_SHIFT_SMS_TEMPLATE,
        callout_multi_shift_template: DEFAULT_CALLOUT_MULTI_SHIFT_SMS_TEMPLATE,
        callout_multi_shift_all_template: MULTI_SHIFT_ALL_SHIFTS_SMS_TEMPLATE,
        multi_shift_item_template: DEFAULT_CALLOUT_MULTI_SHIFT_ITEM_TEMPLATE,
    };
}

export function applyEscalationPolicyToSteps({
    policy,
    steps,
    originalSteps,
    templateSteps,
    outreachType,
    supportsShiftRecurrence = false,
}: {
    readonly policy: EscalationPolicy;
    readonly steps: ReadonlyArray<OutreachStep>;
    readonly originalSteps?: ReadonlyArray<OutreachStep>;
    readonly templateSteps?: ReadonlyArray<OutreachStep>;
    readonly outreachType: OutreachType;
    readonly supportsShiftRecurrence?: boolean;
}): Array<OutreachStep> {
    const needsSms = policy === 'sms_only' || policy === 'sms_and_voice';
    const needsVoice = policy === 'voice_only' || policy === 'sms_and_voice';
    const orderSource = pickOrderSource({
        policy,
        steps,
        originalSteps,
        templateSteps,
    });
    const currentSmsSteps = steps.filter((step) => step.step_type === 'sms');
    const currentVoiceSteps = steps.filter(
        (step) => step.step_type === 'voice',
    );
    const originalSmsSteps =
        originalSteps?.filter((step) => step.step_type === 'sms') ?? [];
    const originalVoiceSteps =
        originalSteps?.filter((step) => step.step_type === 'voice') ?? [];
    const templateSmsSteps =
        templateSteps?.filter((step) => step.step_type === 'sms') ?? [];
    const templateVoiceSteps =
        templateSteps?.filter((step) => step.step_type === 'voice') ?? [];
    const smsStepsForPolicy =
        currentSmsSteps.length > 0
            ? [...currentSmsSteps]
            : originalSmsSteps.length > 0
              ? [...originalSmsSteps]
              : [...templateSmsSteps];
    const voiceStepsForPolicy =
        currentVoiceSteps.length > 0
            ? [...currentVoiceSteps]
            : originalVoiceSteps.length > 0
              ? [...originalVoiceSteps]
              : [...templateVoiceSteps];

    const nextSteps: Array<OutreachStep> = [];
    for (const step of orderSource) {
        if (!needsStepType(step.step_type, policy)) {
            continue;
        }

        const nextStep = takeNextStepOfType({
            typeSteps:
                step.step_type === 'sms'
                    ? smsStepsForPolicy
                    : voiceStepsForPolicy,
        });
        if (nextStep) {
            nextSteps.push(nextStep);
        }
    }

    if (needsSms) {
        nextSteps.push(...smsStepsForPolicy);
        if (!nextSteps.some((step) => step.step_type === 'sms')) {
            const fallbackSmsSteps = getFallbackOutreachStepsForType(
                'sms',
                steps,
                originalSteps,
                templateSteps,
            );
            if (fallbackSmsSteps.length > 0) {
                nextSteps.push(...fallbackSmsSteps);
            } else {
                nextSteps.push(
                    createDefaultOutreachStep(
                        'sms',
                        outreachType,
                        steps,
                        originalSteps,
                        templateSteps,
                        supportsShiftRecurrence,
                    ),
                );
            }
        }
    }

    if (needsVoice) {
        nextSteps.push(...voiceStepsForPolicy);
        if (!nextSteps.some((step) => step.step_type === 'voice')) {
            const fallbackVoiceSteps = getFallbackOutreachStepsForType(
                'voice',
                steps,
                originalSteps,
                templateSteps,
            );
            if (fallbackVoiceSteps.length > 0) {
                nextSteps.push(...fallbackVoiceSteps);
            } else {
                nextSteps.push(
                    createDefaultOutreachStep(
                        'voice',
                        outreachType,
                        steps,
                        originalSteps,
                        templateSteps,
                        supportsShiftRecurrence,
                    ),
                );
            }
        }
    }

    return normalizeOutreachStepDelays(nextSteps, outreachType);
}

export function appendOutreachStep({
    stepType,
    steps,
    originalSteps,
    templateSteps,
    outreachType,
    supportsShiftRecurrence = false,
}: {
    readonly stepType: OutreachStep['step_type'];
    readonly steps: ReadonlyArray<OutreachStep>;
    readonly originalSteps?: ReadonlyArray<OutreachStep>;
    readonly templateSteps?: ReadonlyArray<OutreachStep>;
    readonly outreachType: OutreachType;
    readonly supportsShiftRecurrence?: boolean;
}): Array<OutreachStep> {
    return normalizeOutreachStepDelays(
        [
            ...steps,
            createDefaultOutreachStep(
                stepType,
                outreachType,
                steps,
                originalSteps,
                templateSteps,
                supportsShiftRecurrence,
            ),
        ],
        outreachType,
    );
}

export function removeOutreachStep(
    steps: ReadonlyArray<OutreachStep>,
    stepIndex: number,
    outreachType: OutreachType,
): Array<OutreachStep> {
    return normalizeOutreachStepDelays(
        steps.filter((_, index) => index !== stepIndex),
        outreachType,
    );
}

export function getStepVoiceOpeningLineValue({
    step,
    outreachType,
    isMultiShift,
}: {
    readonly step: OutreachStep | undefined;
    readonly outreachType: OutreachType;
    readonly isMultiShift: boolean;
}): string | null | undefined {
    if (!step) {
        return undefined;
    }
    if (usesSplitVoiceOpeningLine(outreachType, isMultiShift)) {
        return (
            nullifyEmpty(step.multi_shift_prompt_alteration) ??
            getLegacyMultiShiftOpeningLine({
                openingLine: step.prompt_alteration,
                outreachType,
            })
        );
    }
    return step.prompt_alteration;
}

function getMetadataOpeningLineValue(
    metadataSchema: Record<string, unknown> | undefined,
    key: 'opening_line' | 'multi_shift_opening_line',
): string | null {
    const rawValue = metadataSchema?.[key];
    return typeof rawValue === 'string' && rawValue.trim().length > 0
        ? rawValue
        : null;
}

// Stored step order is preserved on hydration. New steps are appended by the
// editor, so an explicitly reordered sequence is not silently changed on reload.
export function hydrateTemplateEditorSteps({
    steps,
    outreachType,
    metadataSchema,
}: {
    readonly steps: ReadonlyArray<OutreachStep>;
    readonly outreachType: OutreachType;
    readonly metadataSchema: Record<string, unknown> | undefined;
}): Array<OutreachStep> {
    const normalizedSteps = normalizeOutreachStepDelays(steps, outreachType);

    if (outreachType === 'callout' || outreachType === 'recurring_shift') {
        // A callout always surfaces its SMS message + phone in the editor, even
        // when an org configured the callout voice-only. Prepend a default SMS
        // step when one is missing so One shift / Multiple shifts always show
        // the message editor (matching the recurring and other tabs).
        const stepsWithSms =
            outreachType === 'callout' &&
            !normalizedSteps.some((step) => step.step_type === 'sms')
                ? normalizeOutreachStepDelays(
                      [
                          createDefaultOutreachStep(
                              'sms',
                              outreachType,
                              normalizedSteps,
                              undefined,
                              undefined,
                          ),
                          ...normalizedSteps,
                      ],
                      outreachType,
                  )
                : normalizedSteps;

        const legacySingleShiftOpeningLine = getMetadataOpeningLineValue(
            metadataSchema,
            'opening_line',
        );
        const legacyMultiShiftOpeningLine = getMetadataOpeningLineValue(
            metadataSchema,
            'multi_shift_opening_line',
        );
        let appliedLegacyVoiceDefaults = false;

        return stepsWithSms.map((step) => {
            if (step.step_type !== 'voice' || appliedLegacyVoiceDefaults) {
                return step;
            }

            appliedLegacyVoiceDefaults = true;
            return {
                ...step,
                prompt_alteration:
                    step.prompt_alteration ?? legacySingleShiftOpeningLine,
                multi_shift_prompt_alteration:
                    step.multi_shift_prompt_alteration ??
                    legacyMultiShiftOpeningLine,
            };
        });
    }

    // Note: PHO-13859 used to scaffold a synthetic step for any missing
    // clock reminder channel here, so both sections always rendered. That
    // resurrected steps the user deliberately removed — Remove + Save
    // persisted one channel, this hydrate re-added the other, and the next
    // save silently made it real again (re-enabling that channel's
    // reminders). A missing channel is configuration, not an oversight;
    // the "Add step" buttons are the affordance for adding one back.
    const legacyOpeningLine = getMetadataOpeningLineValue(
        metadataSchema,
        'opening_line',
    );
    if (!legacyOpeningLine) {
        return normalizedSteps;
    }

    return normalizedSteps.map((step) => {
        if (step.step_type !== 'voice') {
            return step;
        }

        return {
            ...step,
            prompt_alteration: step.prompt_alteration ?? legacyOpeningLine,
        };
    });
}

/**
 * Resolve the voice opening line for outreach submission.
 *
 * Prefers the first voice step's active opening-line field when the user
 * has explicitly set one, falling back to the template-level opening
 * line. Returns null when neither source provides a non-empty value.
 *
 * `hasStoredOpeningLine` reports whether the step itself carried the line,
 * so a caller can tell stored content from a line derived for display.
 */
export function resolveVoiceOpeningLine({
    steps,
    templateOpeningLine,
    outreachType,
    isMultiShift,
}: {
    readonly steps: ReadonlyArray<OutreachStep>;
    readonly templateOpeningLine: string | null | undefined;
    readonly outreachType: OutreachType;
    readonly isMultiShift: boolean;
}): {
    readonly trimmedOpeningLine: string | null;
    readonly firstVoiceStepIndex: number;
    readonly hasStoredOpeningLine: boolean;
} {
    const firstVoiceStepIndex = steps.findIndex(
        (step) => step.step_type === 'voice',
    );
    const firstVoiceStep =
        firstVoiceStepIndex >= 0 ? steps[firstVoiceStepIndex] : undefined;
    const activeStepOpeningLine = getStepVoiceOpeningLineValue({
        step: firstVoiceStep,
        outreachType,
        isMultiShift,
    });
    const hasStoredOpeningLine =
        activeStepOpeningLine !== null && activeStepOpeningLine !== undefined;
    const trimmedOpeningLine = hasStoredOpeningLine
        ? activeStepOpeningLine?.trim() || null
        : templateOpeningLine?.trim() ||
          getDefaultVoiceOpeningLine({
              outreachType,
              isMultiShift,
          }).trim() ||
          null;
    return { trimmedOpeningLine, firstVoiceStepIndex, hasStoredOpeningLine };
}

function nullifyEmpty(value: string | null | undefined): string | null {
    if (value === null || value === undefined) {
        return null;
    }
    return value.trim().length === 0 ? null : value;
}

/**
 * A step that may carry its persisted row id and promoted v3 columns.
 *
 * The id makes a step-list save diff by id instead of reading as
 * remove-and-insert. The promoted fields are editor-opaque but must be
 * round-tripped: the step write is declarative, so dropping them clears
 * whatever the agent set — and `opening_line` is a live rendering input.
 */
export type IdentifiedOutreachStep = OutreachStep & {
    readonly id?: string | null;
    readonly ordinal?: number | null;
    readonly opening_line?: string | null;
    readonly incentive_amount_usd?: string | null;
    readonly incentive_description?: string | null;
};

type SerializedOutreachStep = {
    readonly id?: string | null;
    readonly opening_line?: string | null;
    readonly incentive_amount_usd?: string | null;
    readonly incentive_description?: string | null;
    readonly step_type: 'sms' | 'voice';
    readonly delay_in_minutes: number;
    readonly message_template?: string | null;
    readonly prompt_alteration?: string | null;
    readonly multi_shift_prompt_alteration?: string | null;
    readonly callout_template?: string | null;
    readonly callout_multi_shift_template?: string | null;
    readonly callout_multi_shift_all_template?: string | null;
    readonly consolidated_shift_template?: string | null;
    readonly multi_shift_item_template?: string | null;
    readonly group_template?: string | null;
    readonly per_group_template?: string | null;
    readonly single_client_template?: string | null;
};

/**
 * Serialize local OutreachStep objects into the shape expected by the
 * outreach creation API. Handles voice opening-line overrides,
 * empty-string normalization, and outreach-type-specific SMS fields.
 */
export function serializeStepsForSubmission({
    steps,
    outreachType,
    trimmedOpeningLine,
    firstVoiceStepIndex,
    isMultiShift,
    includeStepIds = false,
}: {
    readonly steps: ReadonlyArray<IdentifiedOutreachStep>;
    readonly outreachType: OutreachType;
    readonly trimmedOpeningLine: string | null;
    readonly firstVoiceStepIndex: number;
    readonly isMultiShift: boolean;
    readonly includeStepIds?: boolean;
}): Array<SerializedOutreachStep> {
    const isCalloutOrRecurring =
        outreachType === 'callout' || outreachType === 'recurring_shift';
    const isMultiShiftWithSplit = isCalloutOrRecurring && isMultiShift;
    const isRecurring = outreachType === 'recurring_shift';

    return steps.map((step, index) => {
        const isFirstVoice =
            step.step_type === 'voice' && index === firstVoiceStepIndex;

        // Voice opening-line fields: override the active field on the
        // first voice step with the resolved opening line, normalize
        // empty strings to null on all voice steps.
        const promptAlteration =
            isFirstVoice && !isMultiShiftWithSplit && trimmedOpeningLine
                ? trimmedOpeningLine
                : nullifyEmpty(step.prompt_alteration);
        const multiShiftPromptAlteration = isMultiShiftWithSplit
            ? isFirstVoice && trimmedOpeningLine
                ? trimmedOpeningLine
                : nullifyEmpty(step.multi_shift_prompt_alteration)
            : null;

        const serialized: SerializedOutreachStep = {
            ...(includeStepIds
                ? {
                      id: step.id ?? null,
                      opening_line: step.opening_line ?? null,
                      incentive_amount_usd: step.incentive_amount_usd ?? null,
                      incentive_description: step.incentive_description ?? null,
                  }
                : {}),
            step_type: step.step_type,
            delay_in_minutes: step.delay_in_minutes,
            message_template: step.message_template ?? null,
            prompt_alteration: promptAlteration,
            multi_shift_prompt_alteration: multiShiftPromptAlteration,
            multi_shift_item_template: step.multi_shift_item_template ?? null,
            single_client_template: step.single_client_template ?? null,
            consolidated_shift_template:
                step.consolidated_shift_template ?? null,
        };

        if (!isRecurring) {
            return {
                ...serialized,
                callout_template: step.callout_template ?? null,
                callout_multi_shift_template:
                    step.callout_multi_shift_template ?? null,
                callout_multi_shift_all_template:
                    step.callout_multi_shift_all_template ?? null,
                group_template: step.group_template ?? null,
                per_group_template: step.per_group_template ?? null,
            };
        }

        return {
            ...serialized,
            group_template: step.group_template ?? null,
            per_group_template: step.per_group_template ?? null,
        };
    });
}

const SMS_TEMPLATE_FIELDS = [
    'message_template',
    'callout_template',
    'callout_multi_shift_template',
    'callout_multi_shift_all_template',
    'consolidated_shift_template',
    'multi_shift_item_template',
    'group_template',
    'per_group_template',
    'single_client_template',
] as const satisfies ReadonlyArray<keyof OutreachStep>;

export type OutreachSmsTemplateEntry = {
    readonly field: (typeof SMS_TEMPLATE_FIELDS)[number];
    readonly stepIndex: number;
    readonly value: string;
};

/** Return persisted SMS template fields with their source step and field. */
export function getOutreachSmsTemplateEntries(
    steps: ReadonlyArray<OutreachStep>,
): Array<OutreachSmsTemplateEntry> {
    return steps.flatMap((step, stepIndex) =>
        step.step_type === 'sms'
            ? SMS_TEMPLATE_FIELDS.flatMap((field) => {
                  const value = step[field];
                  return typeof value === 'string'
                      ? [{ field, stepIndex, value }]
                      : [];
              })
            : [],
    );
}

/** Return persisted SMS template text without inspecting voice prompt fields. */
export function getOutreachSmsTemplateTexts(
    steps: ReadonlyArray<OutreachStep>,
): Array<string> {
    return getOutreachSmsTemplateEntries(steps).map((entry) => entry.value);
}

/**
 * Return SMS template text that is new or edited relative to the saved
 * template.
 *
 * Callers use this to prompt only about templates the user authored this
 * session, so an untouched default never raises a prompt the user cannot
 * honestly answer.
 *
 * Entries are matched as a multiset of (field, text) pairs rather than by step
 * position. Position would make a reorder look like a full rewrite; matching on
 * text alone would let text copied from one field into another slip through
 * because that exact string was already saved somewhere. Counting occurrences
 * also catches a duplicated step, where the pair is already saved but is now
 * about to send twice.
 */
export function getChangedOutreachSmsTemplateTexts(
    steps: ReadonlyArray<OutreachStep>,
    savedSteps: ReadonlyArray<OutreachStep>,
): Array<string> {
    const entryKey = (entry: OutreachSmsTemplateEntry) =>
        `${entry.field} ${entry.value}`;

    const unclaimedSavedEntries = new Map<string, number>();
    for (const entry of getOutreachSmsTemplateEntries(savedSteps)) {
        const key = entryKey(entry);
        unclaimedSavedEntries.set(
            key,
            (unclaimedSavedEntries.get(key) ?? 0) + 1,
        );
    }

    const changedTexts: Array<string> = [];
    for (const entry of getOutreachSmsTemplateEntries(steps)) {
        const key = entryKey(entry);
        const unclaimed = unclaimedSavedEntries.get(key) ?? 0;
        if (unclaimed > 0) {
            unclaimedSavedEntries.set(key, unclaimed - 1);
        } else {
            changedTexts.push(entry.value);
        }
    }
    return changedTexts;
}
